import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas, qaSchema } from "@/lib/seo";

const UPDATED = "2026-09-29";
const ANSWER =
  "Shopify SEO braucht in der Regel drei bis sechs Monate, bis erste spürbare Platzierungen kommen. Technische Verbesserungen wirken oft schon nach wenigen Wochen, Inhalte und Verlinkungen brauchen länger. Wer schneller Besucher braucht, kombiniert SEO mit Google Ads.";

const SITE = "https://shopify-freelancer.net";
const PATH = "/fragen/wie-lange-dauert-shopify-seo";
const TITLE = "Wie lange dauert Shopify SEO?";
const DESCRIPTION =
  "Realistische Zeitrahmen für Shopify SEO: was in Wochen wirkt, was Monate braucht und wie Sie die Wartezeit sinnvoll überbrücken.";

const faqs = [
  {
    q: "Wann sehe ich erste Ergebnisse?",
    a: "Technische Korrekturen wie Ladezeit oder fehlerhafte Weiterleitungen wirken oft nach zwei bis sechs Wochen. Neue Inhalte brauchen meist drei bis sechs Monate.",
  },
  {
    q: "Warum dauert SEO so lange?",
    a: "Google muss Seiten neu lesen, bewerten und mit Wettbewerbern vergleichen. Vertrauen entsteht über Zeit, vor allem durch gute Inhalte und echte Verlinkungen.",
  },
  {
    q: "Kann man SEO beschleunigen?",
    a: "Saubere Technik, klare Seitenstruktur und Inhalte, die echte Fragen beantworten, helfen. Abkürzungen wie gekaufte Links schaden eher.",
  },
  {
    q: "Was mache ich in der Wartezeit?",
    a: "Google Ads bringt sofort Besucher und zeigt, welche Suchbegriffe Käufer bringen. Diese Begriffe bauen Sie dann organisch aus.",
  },
];

export const Route = createFileRoute("/fragen/wie-lange-dauert-shopify-seo")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: [
      ...articleSchemas({
        title: TITLE,
        description: DESCRIPTION,
        path: PATH,
        faqs,
        section: "Shopify SEO",
        updated: UPDATED,
        breadcrumb: { name: "Fragen", path: "/fragen" },
      }),
      qaSchema({
        question: "Wie lange dauert Shopify SEO?",
        answer: ANSWER,
        path: PATH,
        updated: UPDATED,
      }),
    ],
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Frage · SEO"
      title="Wie lange dauert Shopify SEO?"
      intro="Kurz gesagt: drei bis sechs Monate für spürbare Platzierungen. Technik wirkt schneller, Inhalte und Vertrauen brauchen Zeit."
      source="frage-seo-dauer"
      answer={ANSWER}
      updated={UPDATED}
      breadcrumb={{ to: "/fragen", label: "Fragen" }}
      sections={[
        {
          h: "Was schnell wirkt",
          p: [],
          list: [
            "Ladezeit und technische Fehler: oft zwei bis sechs Wochen",
            "Titel und Beschreibungen korrigieren: wenige Wochen",
            "Defekte Weiterleitungen reparieren: wenige Wochen",
          ],
        },
        {
          h: "Was Zeit braucht",
          p: [],
          list: [
            "Neue Ratgeber- und Kategorieseiten: drei bis sechs Monate",
            "Vertrauen und Verlinkungen aufbauen: sechs bis zwölf Monate",
            "Umkämpfte Suchbegriffe: oft über ein Jahr",
          ],
        },
        {
          h: "SEO und Ads klug kombinieren",
          p: [
            "Ads liefern sofort Besucher und zeigen, welche Suchbegriffe wirklich kaufen. Genau diese Begriffe bauen Sie dann organisch aus, statt auf gut Glück zu schreiben.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-seo", label: "Shopify SEO im Detail" },
        { to: "/ratgeber", label: "Ratgeber" },
        { to: "/fragen/warum-kauft-niemand-in-meinem-shop", label: "Warum kauft niemand?" },
      ]}
    />
  );
}
